'use client';

import { useEffect } from 'react';
import { countUncaughtError, logUncaughtError } from '@/app/common/components/ErrorBoundary/utils.ts';
import { isChunkLoadError, isNetworkFailure, splitStack } from '@/utils/errors.ts';
import { safeStringifyForLog } from '@/utils/safeStringifyForLog.ts';
import type { UncaughtKind, UncaughtOrigin } from '@/app/common/components/ErrorBoundary/uncaughtErrorTypes.ts';

// What a browser reports instead of a cross-origin script's error, with no file, line, stack or message behind it.
const OPAQUE_CROSS_ORIGIN_ERROR = /^Script error\.?$/;

// Our bundle only: an inline script reports the document URL, and so does every advert tag GTM injects inline.
const OWN_CODE = '/_next/';
const EXTENSION_FRAME = /(?:chrome|moz|safari|safari-web)-extension:|webkit-masked-url:/;
// WebKit names scripts a native app injects into its webview user-script:N; another team owns them.
const APP_WEBVIEW_FRAME = /user-script:\d/;
const STACK_FRAME = /:\d+:\d+/;
// Anything we cannot prove is someone else's stays in Loki; only these are counted instead.
const COUNTED_ORIGINS = new Set<UncaughtOrigin>(['opaque', 'extension', 'third-party', 'network']);

// Another realm's Error fails instanceof, so match on shape instead.
const isErrorLike = (reason: unknown): reason is Error =>
  reason instanceof Error || typeof (reason as Error | undefined)?.message === 'string';

const originOfFrames = (frames: string, message: string): UncaughtOrigin => {
  const webviewAt = frames.search(APP_WEBVIEW_FRAME);
  const ownAt = frames.indexOf(OWN_CODE);
  // Whichever code sits higher in the stack threw, so it owns the error.
  if (webviewAt !== -1 && (ownAt === -1 || webviewAt < ownAt)) return 'app-webview';
  if (ownAt !== -1) return 'tsx';
  if (EXTENSION_FRAME.test(frames)) return 'extension';
  if (STACK_FRAME.test(frames)) return 'third-party';
  return isNetworkFailure(message) ? 'network' : 'no-stack';
};

const originOfError = (error: Error, thrownAt = ''): UncaughtOrigin =>
  isChunkLoadError(error) ? 'tsx' : originOfFrames(`${thrownAt}\n${splitStack(error).frames}`, error.message);

const rejectionOrigin = (reason: unknown): UncaughtOrigin =>
  isErrorLike(reason) ? originOfError(reason) : 'non-error';

const errorOrigin = ({ message, filename, lineno, colno, error }: ErrorEvent): UncaughtOrigin => {
  if (OPAQUE_CROSS_ORIGIN_ERROR.test(message)) return 'opaque';
  // Where it was thrown, which a rethrown Error's own stack does not show.
  const thrownAt = `${filename}:${lineno}:${colno}`;
  return isErrorLike(error) ? originOfError(error, thrownAt) : originOfFrames(thrownAt, message);
};

// Error boundaries only see render and commit. A throw in an event handler or an async callback leaves
// the page looking healthy with a dead control and no signal, so report those here instead.
const guarded = (reportFn: () => void) => {
  try {
    reportFn();
  } catch {
    // reporting must never become the failure it is reporting
  }
};

// The constructor name keeps what JSON drops: an Event reason serialises to little more than {"isTrusted":true}.
const typeName = (value: object) => {
  try {
    return String(value.constructor?.name ?? 'Object');
  } catch {
    return 'Object';
  }
};

const describeValue = (value: unknown) =>
  typeof value === 'object' && value !== null ? `${typeName(value)} ${safeStringifyForLog(value)}` : String(value);

// A wrapper Error must not carry this file's stack, or the log would point at the reporter instead of the cause.
const asError = (value: unknown) =>
  isErrorLike(value) ? value : Object.assign(new Error(describeValue(value)), { stack: '' });

const report = (kind: UncaughtKind, origin: UncaughtOrigin, error: unknown, source?: string) =>
  guarded(() => logUncaughtError(kind, origin, asError(error), source));

const handle = (kind: UncaughtKind, classify: () => UncaughtOrigin, cause: unknown, source?: string) =>
  guarded(() => {
    let origin: UncaughtOrigin;
    try {
      origin = classify();
    } catch {
      // A value that throws when read, e.g. a revoked Proxy, proves nothing, so it stays visible
      report(kind, 'non-error', 'Unreadable error value', source);
      return;
    }

    if (COUNTED_ORIGINS.has(origin)) {
      countUncaughtError(kind, origin);
      return;
    }

    report(kind, origin, cause, source);
  });

const GlobalErrorReporter = () => {
  useEffect(() => {
    // A resource that failed to load dispatches a bare Event here, carrying no error at all.
    const onError = (event: Event) => {
      if (!(event instanceof ErrorEvent)) return;
      const source = `at:${event.filename}:${event.lineno}:${event.colno}`;
      handle('error', () => errorOrigin(event), event.error ?? event.message, source);
    };
    const onRejection = ({ reason }: PromiseRejectionEvent) =>
      handle('unhandledrejection', () => rejectionOrigin(reason), reason);

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
};

export default GlobalErrorReporter;
